import { Paper, Stack, FormControl, InputLabel, OutlinedInput } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { memo, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import CustomSelect from '../../common/Select/CustomSelect';
import DateRangePicker from '../../common/DatePicker/DateRangePicker';
import CorporationSelectionModal from '../../common/Modal/CorporationSelectionModal';
import {
  getManagementGroupsList,
  getCorporationDetails,
  getDepartments,
  getJobTitles,
} from '../../../api';
import { mapCorporationPeriodDates } from '../../../HelperFunctions/ViewTransaction/dateRangeOptions';


const LeftFilterPanel = ({
  filters = {},
  fromLabel,
  toLabel,
  transactionOptions = [],
  managementGroupOptions,
  onFilterChange,
  onApplyAndGenerateCorporations,
}) => {
  const [isCorpModalOpen, setIsCorpModalOpen] = useState(false);
  const [fallbackOptions, setFallbackOptions] = useState([
    { DisplayText: 'ALL', ValueCode: 'ALL' },
  ]);
  const [departmentOptions, setDepartmentOptions] = useState([
    { label: 'ALL', value: 'all' },
  ]);
  const [jobTitleOptions, setJobTitleOptions] = useState([
    { label: 'ALL', value: 'all' },
  ]);

  const onFilterChangeRef = useRef(onFilterChange);
  onFilterChangeRef.current = onFilterChange;

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const jobTitlesLoadedRef = useRef(false);

  useEffect(() => {
    if (!managementGroupOptions || managementGroupOptions.length === 0) {
      getManagementGroupsList().then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setFallbackOptions(data);
        }
      });
    }
  }, [managementGroupOptions]);

  // Load Job Titles from API once on mount
  useEffect(() => {
    if (jobTitlesLoadedRef.current) return;
    jobTitlesLoadedRef.current = true;

    const loadJobTitles = async () => {
      try {
        const data = await getJobTitles();
        if (Array.isArray(data) && data.length > 0) {
          const list = [{ label: 'ALL', value: 'all' }, ...data];
          setJobTitleOptions(list);
          const allIds = data.map((d) => d.value).filter(Boolean);
          onFilterChangeRef.current('allJobTitleIds', allIds);
          const currentFilters = filtersRef.current;
          if (currentFilters.jobTitle && String(currentFilters.jobTitle).toLowerCase() !== 'all') {
            const match = list.find(
              (o) =>
                String(o.label).toLowerCase() === String(currentFilters.jobTitle).toLowerCase() ||
                String(o.label).toLowerCase() === String(currentFilters.jobTitleName).toLowerCase()
            );
            if (match && match.value !== 'all' && currentFilters.jobTitle !== match.value) {
              onFilterChangeRef.current('jobTitle', match.value);
              onFilterChangeRef.current('jobTitleName', match.label);
            }
          }
        }
      } catch (err) {
        console.error('Failed to load job titles:', err);
      }
    };

    loadJobTitles();
  }, []);

  // Compute a stable unique key for corporation IDs (handles single or multiple selected corps)
  const corpIdsKey = useMemo(() => {
    let ids = [];
    if (filters.selectedCorporationRecords && filters.selectedCorporationRecords.length > 0) {
      ids = filters.selectedCorporationRecords
        .map((r) => r?.CorporationID || r?.corpId)
        .filter(Boolean);
    } else if (filters.selectedCorporationIds && filters.selectedCorporationIds.length > 0) {
      ids = filters.selectedCorporationIds
        .map((id) => id.split('_')[0])
        .filter(Boolean);
    }
    if (ids.length === 0) return 'ALL';
    return [...new Set(ids)].sort().join(',');
  }, [filters.selectedCorporationIds, filters.selectedCorporationRecords]);

  const lastFetchedCorpKeyRef = useRef(null);

  // Load Departments from API ONLY when selected corporation actually changes
  useEffect(() => {
    if (lastFetchedCorpKeyRef.current === corpIdsKey) return;
    lastFetchedCorpKeyRef.current = corpIdsKey;

    const loadDepartments = async () => {
      try {
        const data = await getDepartments();
        const allDepts = Array.isArray(data) ? data : [];

        if (allDepts.length > 0) {
          const list = [{ label: 'ALL', value: 'all' }, ...allDepts];
          setDepartmentOptions(list);
          const allIds = allDepts.map((d) => d.value).filter(Boolean);
          onFilterChangeRef.current('allDepartmentIds', allIds);

          const currentFilters = filtersRef.current;
          if (currentFilters.department && String(currentFilters.department).toLowerCase() !== 'all') {
            const match = list.find(
              (o) =>
                String(o.label).toLowerCase() === String(currentFilters.department).toLowerCase() ||
                String(o.label).toLowerCase() === String(currentFilters.departmentName).toLowerCase()
            );
            if (match && match.value !== 'all' && currentFilters.department !== match.value) {
              onFilterChangeRef.current('department', match.value);
              onFilterChangeRef.current('departmentName', match.label);
            }
          }
        } else {
          setDepartmentOptions([{ label: 'ALL', value: 'all' }]);
          onFilterChangeRef.current('allDepartmentIds', []);
        }
      } catch (err) {
        console.error('Failed to load departments:', err);
        setDepartmentOptions([{ label: 'ALL', value: 'all' }]);
        onFilterChangeRef.current('allDepartmentIds', []);
      }
    };

    loadDepartments();
  }, [corpIdsKey]);


  const activeMgmtOptions = useMemo(() => {
    if (managementGroupOptions && managementGroupOptions.length > 0) {
      return managementGroupOptions;
    }
    return fallbackOptions;
  }, [managementGroupOptions, fallbackOptions]);

  const displayCorpValue = useMemo(() => {
    if (filters.selectedCorporationIds && filters.selectedCorporationIds.length > 0) {
      if (filters.selectedCorporationIds.length === 1) {
        return filters.corporation || '1 Corporation Selected';
      }
      return `${filters.selectedCorporationIds.length} Corporations Selected`;
    }
    if (filters.corporation && filters.corporation !== 'all' && filters.corporation !== 'Corporations') {
      return filters.corporation;
    }
    return 'Corporations';
  }, [filters.selectedCorporationIds, filters.corporation]);

  const handleApplyCorporations = useCallback(
    async (selectedIds, selectedRecords) => {
      onFilterChange('selectedCorporationIds', selectedIds);
      onFilterChange('selectedCorporationRecords', selectedRecords);
      // Reset department to 'all' on changing corporation
      onFilterChange('department', 'all');
      onFilterChange('departmentName', 'ALL');
      if (selectedIds.length === 0) {
        onFilterChange('corporation', 'Corporations');
      } else if (selectedIds.length === 1) {
        const singleRecord = selectedRecords[0];
        const corpName = singleRecord?.CorporationName || singleRecord?.dbaName || '1 Corporation Selected';
        onFilterChange('corporation', corpName);

        const rawCorpId = singleRecord?.CorporationID || singleRecord?.id?.split('_')[0];
        if (rawCorpId) {
          try {
            const details = await getCorporationDetails(rawCorpId);
            if (details && details.PeriodStartDate) {
              const mapped = mapCorporationPeriodDates(details.PeriodStartDate, details.PeriodEndDate);
              if (mapped) {
                onFilterChange('fromDate', mapped.fromDate);
                onFilterChange('toDate', mapped.toDate);
                onFilterChange('transactionView', '11');
              }
            }
          } catch (err) {
            console.error('Error fetching corporation details:', err);
          }
        }
      } else {
        onFilterChange('corporation', `${selectedIds.length} Corporations Selected`);
      }
    },
    [onFilterChange]
  );

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2, height: '90vh', bgcolor: 'background.paper' }}>
        <Stack spacing={2}>
          <CustomSelect
            id="management-group"
            label="Management Groups"
            value={filters.managementGroup}
            options={activeMgmtOptions}
            onChange={(value) => onFilterChange('managementGroup', value)}
          />

          {/* Corporation Field opening Corporation Selection Screen */}
          <FormControl fullWidth size="small" sx={{ position: 'relative' }}>
            <InputLabel id="corporation-label" sx={{ fontSize: '0.875rem' }}>
              Corporation
            </InputLabel>
            <OutlinedInput
              id="corporation"
              label="Corporation"
              value={displayCorpValue}
              readOnly
              onClick={(e) => {
                e.target?.blur();
                setIsCorpModalOpen(true);
              }}
              onFocus={(e) => {
                e.target?.blur();
              }}
              endAdornment={
                <ArrowDropDownIcon
                  sx={{
                    color: 'rgba(0, 0, 0, 0.54)',
                    position: 'absolute',
                    right: 7,
                    top: 'calc(50% - 12px)',
                    pointerEvents: 'none',
                  }}
                />
              }
              sx={{
                fontSize: '0.7rem',
                fontWeight: 550,
                height: 40,
                cursor: 'pointer',
                backgroundColor: '#f5f5f5',
                '& .MuiOutlinedInput-input': {
                  cursor: 'pointer',
                  paddingTop: 1.5,
                  paddingBottom: 1.5,
                  paddingRight: '36px',
                  fontWeight: 550,
                  fontSize: '0.7rem',
                },
                '&:hover': {
                  backgroundColor: '#f0f0f0',
                },
              }}
            />
          </FormControl>

          <CustomSelect
            id="transaction-view"
            label="View Transaction"
            value={filters.transactionView}
            options={transactionOptions}
            onChange={(value) => onFilterChange('transactionView', value)}
          />

          <DateRangePicker
            fromLabel={fromLabel}
            toLabel={toLabel}
            fromDate={filters.fromDate}
            toDate={filters.toDate}
            onFromDateChange={(date) => onFilterChange('fromDate', date)}
            onToDateChange={(date) => onFilterChange('toDate', date)}
          />

          <CustomSelect
            id="department"
            label="Department"
            value={filters.department || 'all'}
            options={departmentOptions}
            onChange={(value) => {
              const selectedOpt = departmentOptions.find((o) => o.value === value);
              onFilterChange('department', value);
              onFilterChange('departmentName', selectedOpt ? selectedOpt.label : value);
            }}
          />

          <CustomSelect
            id="job-title"
            label="Job Title"
            value={filters.jobTitle || 'all'}
            options={jobTitleOptions}
            onChange={(value) => {
              const selectedOpt = jobTitleOptions.find((o) => o.value === value);
              onFilterChange('jobTitle', value);
              onFilterChange('jobTitleName', selectedOpt ? selectedOpt.label : value);
            }}
          />


        </Stack>
      </Paper>

      {/* Corporation Selection Modal Screen */}
      <CorporationSelectionModal
        open={isCorpModalOpen}
        onClose={() => setIsCorpModalOpen(false)}
        selectedCorporationIds={filters.selectedCorporationIds || []}
        onApply={handleApplyCorporations}
        onApplyAndGenerate={onApplyAndGenerateCorporations}
        initialManagementGroup={filters.managementGroup}
        customManagementGroups={activeMgmtOptions}
      />
    </>
  );
};

export default memo(LeftFilterPanel);
